import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

import { logout } from '~/auth/slices/auth.slice'
import { FamilyData, FamilyInvite, ManageLicenseData } from '~/familyAccount/types/familyAccounts.types'
import { startListening } from '~/redux/listenerMiddleware'
import { RootState } from '~/redux/store'

interface FamilyPackage {
  adminLicense: boolean
  numberOfLicenses: number
  totalPrice: number
  basePrice: number
  accessPrice: number
}
interface FamilyState {
  isMemberSubscriptionRequired: boolean
  isFamilyAdminAuthenticated: boolean
  addedProfile: boolean
  isLinkingAccount: boolean
  editingData?: FamilyData
  editingInviteData?: FamilyInvite
  tempNickname?: string
  viewingStatsId?: number
  statsError?: string
  manageLicenseData?: ManageLicenseData
  familyPackage?: FamilyPackage
  backToPath?: string
}

const initialState: FamilyState = {
  isMemberSubscriptionRequired: false,
  isFamilyAdminAuthenticated: false,
  addedProfile: false,
  isLinkingAccount: false,
  editingData: undefined,
  editingInviteData: undefined,
}

export const familyAccountSlice = createSlice({
  name: 'familyAccount',
  initialState,
  reducers: {
    setMemberSubscriptionRequirementStatus: (state, action: PayloadAction<boolean>) => {
      state.isMemberSubscriptionRequired = action.payload
    },
    setIsFamilyAdminAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isFamilyAdminAuthenticated = action.payload
    },
    setAddedProfile: (state, action: PayloadAction<boolean>) => ({
      ...state,
      addedProfile: action.payload,
    }),
    setLinkingAccount: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isLinkingAccount: action.payload,
    }),
    setEditingData: (state, action: PayloadAction<FamilyData | undefined>) => {
      state.editingData = action.payload
    },
    setEditingInviteData: (state, action: PayloadAction<FamilyInvite | undefined>) => {
      state.editingInviteData = action.payload
    },
    setTempNickname: (state, action: PayloadAction<string | undefined>) => {
      state.tempNickname = action.payload
    },
    setViewingStatsId: (state, action: PayloadAction<number | undefined>) => {
      state.viewingStatsId = action.payload
    },
    setStatsError: (state, action: PayloadAction<string | undefined>) => {
      state.statsError = action.payload
    },
    setManageLicenseData: (state, action: PayloadAction<ManageLicenseData | undefined>) => {
      state.manageLicenseData = action.payload
    },
    setFamilyPackage: (
      state,
      action: PayloadAction<{
        adminLicense: boolean
        numberOfLicenses: number
        totalPrice: number
        basePrice: number
        accessPrice: number
      }>,
    ) => {
      return {
        ...state,
        familyPackage: action.payload,
      }
    },
    /**
     * The licence count on its own, for flows that know how many seats the buyer picked but not what
     * they will cost — reactivation resolves its own price from the repurchase preview, and seeding a
     * campaign price here is the stale-price trap. Prices stay 0, which reads as absent everywhere
     * that checks them, so the package is rebuilt from the real price when one is known.
     */
    setFamilyLicenseCount: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        familyPackage: {
          adminLicense: false,
          totalPrice: 0,
          basePrice: 0,
          accessPrice: 0,
          ...state.familyPackage,
          numberOfLicenses: action.payload,
        },
      }
    },
    resetFamilyPackage: (state) => {
      return {
        ...state,
        familyPackage: undefined,
      }
    },
    resetManageLicenseData: (state) => {
      return {
        ...state,
        manageLicenseData: undefined,
      }
    },
    setBackToPath: (state, action: PayloadAction<string | undefined>) => {
      state.backToPath = action.payload
    },
  },
  extraReducers: (builder) => {
    return builder.addCase(logout, () => initialState)
  },
})
const familyAccountSelector = (state: RootState): FamilyState => state.familyAccount

export const selectIsMemberSubscriptionRequired = (state: RootState): boolean =>
  familyAccountSelector(state).isMemberSubscriptionRequired
export const selectAddedProfile = (state: RootState): boolean => familyAccountSelector(state).addedProfile
export const selectEditingData = (state: RootState): FamilyData | undefined => familyAccountSelector(state).editingData
export const selectEditingInviteData = (state: RootState): FamilyInvite | undefined =>
  familyAccountSelector(state).editingInviteData
export const selectIsLinkingAccount = (state: RootState): boolean => familyAccountSelector(state).isLinkingAccount
export const selectTempNickname = (state: RootState): string | undefined => familyAccountSelector(state).tempNickname
export const selectViewingStatsId = (state: RootState): number | undefined =>
  familyAccountSelector(state).viewingStatsId
export const selectStatsError = (state: RootState): string | undefined => familyAccountSelector(state).statsError
export const selectManageLicenseData = (state: RootState): ManageLicenseData | undefined =>
  familyAccountSelector(state).manageLicenseData
export const selectIsFamilyAdminAuthenticated = (state: RootState): boolean =>
  familyAccountSelector(state).isFamilyAdminAuthenticated
export const selectFamilyPackage = (state: RootState): FamilyPackage | undefined =>
  familyAccountSelector(state).familyPackage
export const selectBackToPath = (state: RootState): string | undefined => familyAccountSelector(state).backToPath

export const {
  setFamilyLicenseCount,
  setMemberSubscriptionRequirementStatus,
  setAddedProfile,
  setEditingData,
  setEditingInviteData,
  setLinkingAccount,
  setTempNickname,
  setViewingStatsId,
  setIsFamilyAdminAuthenticated,
  setStatsError,
  setManageLicenseData,
  setFamilyPackage,
  resetFamilyPackage,
  resetManageLicenseData,
  setBackToPath,
} = familyAccountSlice.actions

// The panel unlock lives in this tab's sessionStorage, not localStorage: a reload or a Stripe/3DS
// round-trip keeps it, a new tab does not. store.ts strips it from the localStorage copy.
const UNLOCK_SESSION_KEY = 'familyAdminUnlocked'

startListening({
  matcher: isAnyOf(setIsFamilyAdminAuthenticated, logout),
  effect: (_action, listenerApi) => {
    try {
      if (listenerApi.getState().familyAccount.isFamilyAdminAuthenticated) {
        sessionStorage.setItem(UNLOCK_SESSION_KEY, '1')
      } else {
        sessionStorage.removeItem(UNLOCK_SESSION_KEY)
      }
    } catch {
      // Storage blocked: the unlock just lasts as long as the page
    }
  },
})

// Runs after every persisted restore, so it wins over the locked copy coming back from localStorage
startListening({
  predicate: (action) => action.type === REHYDRATE,
  effect: (_action, listenerApi) => {
    try {
      if (sessionStorage.getItem(UNLOCK_SESSION_KEY) !== '1') return
    } catch {
      return
    }
    if (!listenerApi.getState().familyAccount.isFamilyAdminAuthenticated) {
      listenerApi.dispatch(setIsFamilyAdminAuthenticated(true))
    }
  },
})

export default familyAccountSlice.reducer
